import { useEffect, useState } from "react";
import { api, setSessionToken, probeReachable } from "./api/client";
import { useAuth } from "./auth/store";
import { useVault } from "./vault/store";
import { useConnectivity } from "./net/connectivity";
import { isTauri, offlineStatus } from "./desktop/bridge";
import { Login } from "./ui/Login";
import { VaultGate } from "./ui/VaultGate";
import { ToastProvider } from "./ui/Toast";
import { ApiErrorPolicy } from "./ui/ApiErrorPolicy";

export default function App() {
  const token = useAuth((s) => s.token);
  const setSession = useAuth((s) => s.setSession);
  const [booting, setBooting] = useState(true);
  // Desktop offline mode (§15): reachable from the login screen too, because the session token
  // lives in sessionStorage and is gone after an app restart — without this, a user with a valid
  // offline cache would be stuck on a sign-in screen that can't work offline.
  const [offlineMode, setOfflineMode] = useState(false);
  const [canOffline, setCanOffline] = useState(false);

  // Track network connectivity (online/offline) app-wide (desktop offline mode, §15).
  useEffect(() => useConnectivity.getState().init(), []);

  // On load, claim a session left by the OIDC callback cookie (SSO redirect → SPA), then — if we
  // still have no session and a usable offline cache exists — decide whether to drop into offline.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { session_token } = await api.claimOidc();
        if (session_token && alive) {
          setSessionToken(session_token);
          const me = await api.me();
          if (alive) setSession(session_token, me.login || "sso");
        }
      } catch {
        /* no pending SSO session */
      }
      // If we now have a session token, the VaultGate handles everything online.
      if (!alive || useAuth.getState().token) {
        if (alive) setBooting(false);
        return;
      }
      // No session: on desktop, offer offline if a usable cache exists; auto-enter when the server
      // is also unreachable (otherwise just expose a "Continue offline" button on the login screen).
      if (isTauri()) {
        const st = await offlineStatus().catch(() => ({ exists: false }) as { exists: boolean; expired?: boolean });
        if (alive && st.exists && !st.expired) {
          setCanOffline(true);
          const reachable = await probeReachable();
          if (alive && !reachable) {
            useVault.getState().enterOfflineLocked();
            setOfflineMode(true);
          }
        }
      }
      if (alive) setBooting(false);
    })();
    return () => {
      alive = false;
    };
  }, [setSession]);

  const goOffline = () => {
    useVault.getState().enterOfflineLocked();
    setOfflineMode(true);
  };

  if (booting) return <div className="grid h-full place-items-center bg-bg text-muted">Loading…</div>;
  return (
    <ToastProvider>
      <ApiErrorPolicy />
      {token || offlineMode ? <VaultGate /> : <Login canOffline={canOffline} onOffline={goOffline} />}
    </ToastProvider>
  );
}
